import { ResourceFieldEditor } from '@/components/crud/ResourceFieldEditor'
import { PlainExplain } from '@/components/primitives/PlainExplain'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { splitEssential } from '@/lib/field-categories'
import { notifyError } from '@/lib/query'
import type {
  ResourceField,
  ResourceRow,
  ResourceSchema
} from '@/lib/resources'
import { createRow, updateRow } from '@/lib/resources'
import { cn } from '@/lib/utils'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  schema: ResourceSchema
  /** When provided → edit mode. Omit for create. */
  initial?: ResourceRow
  onSaved?: (row: ResourceRow) => void
}

function buildInitialValues(
  fields: ResourceField[],
  initial?: ResourceRow
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const field of fields) {
    if (initial && field.name in initial) {
      out[field.name] = initial[field.name]
    } else if (field.widget === 'switch') {
      out[field.name] = false
    } else {
      out[field.name] = null
    }
  }
  return out
}

function FormField({
  field,
  value,
  onChange,
  error,
  disabled
}: {
  field: ResourceField
  value: unknown
  onChange: (v: unknown) => void
  error?: string
  disabled: boolean
}) {
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={field.name} className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-destructive">*</span>}
        </Label>
        {field.hint && <PlainExplain>{field.hint}</PlainExplain>}
      </div>
      <ResourceFieldEditor
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

export function ResourceFormDialog({
  open,
  onOpenChange,
  schema,
  initial,
  onSaved
}: Props) {
  const editable = useMemo(
    () =>
      schema.form_fields.filter((f) => !f.readonly && f.widget !== 'readonly'),
    [schema.form_fields]
  )

  const { core, extra } = useMemo(() => splitEssential(editable), [editable])
  const [showAll, setShowAll] = useState(false)

  const [values, setValues] = useState<Record<string, unknown>>(() =>
    buildInitialValues(editable, initial)
  )
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setValues(buildInitialValues(editable, initial))
      setErrors({})
    }
  }, [open, initial, editable])

  const isEdit = Boolean(initial)

  async function handleSubmit() {
    setSubmitting(true)
    setErrors({})
    try {
      const payload: Record<string, unknown> = {}
      for (const field of editable) {
        const v = values[field.name]
        if (v === undefined) continue
        if (!isEdit && (v === null || v === '') && !field.required) continue
        payload[field.name] = v
      }

      const saved =
        isEdit && initial?.id !== undefined
          ? await updateRow(schema.key, initial.id as string | number, payload)
          : await createRow(schema.key, payload)

      await onSaved?.(saved)
      onOpenChange(false)
      toast.success(
        isEdit ? `${schema.singular} updated` : `${schema.singular} created`
      )
    } catch (err) {
      const apiErr = err as {
        status?: number
        detail?: string
        data?: Record<string, unknown>
      }
      notifyError(isEdit ? 'Could not save' : 'Could not create', err)
      if (apiErr.data && typeof apiErr.data === 'object') {
        const flat: Record<string, string> = {}
        for (const [k, v] of Object.entries(apiErr.data)) {
          flat[k] = Array.isArray(v) ? v.join(', ') : String(v)
        }
        setErrors(flat)
      }
    } finally {
      setSubmitting(false)
    }
  }

  function handleChange(name: string, v: unknown) {
    setValues((prev) => ({ ...prev, [name]: v }))
  }

  const title = isEdit ? `Edit ${schema.singular}` : `New ${schema.singular}`
  const description =
    schema.description ||
    `Fill in the fields below to ${isEdit ? 'update' : 'create'} this ${schema.singular.toLowerCase()}.`

  const totalFields = editable.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex max-h-[85vh] w-[calc(100%-2rem)] flex-col gap-0 p-0',
          totalFields > 10 ? 'sm:max-w-2xl' : 'sm:max-w-lg'
        )}
      >
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-4 px-5 py-4">
            {core.length === 0 && extra.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                This resource has no editable fields.
              </p>
            )}

            {core.length > 0 && (
              <div className="space-y-4">
                {core.map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    value={values[field.name]}
                    onChange={(v) => handleChange(field.name, v)}
                    error={errors[field.name]}
                    disabled={submitting}
                  />
                ))}
              </div>
            )}

            {extra.length > 0 && (
              <div className="border-t pt-3">
                <button
                  type="button"
                  onClick={() => setShowAll(!showAll)}
                  className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showAll ? (
                    <ChevronDown className="size-3" />
                  ) : (
                    <ChevronRight className="size-3" />
                  )}
                  {showAll
                    ? 'Hide extra fields'
                    : `Show ${extra.length} more field${extra.length > 1 ? 's' : ''}`}
                </button>
                {showAll && (
                  <div className="mt-4 space-y-4">
                    {extra.map((field) => (
                      <FormField
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        onChange={(v) => handleChange(field.name, v)}
                        error={errors[field.name]}
                        disabled={submitting}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="border-t bg-muted/40 px-5 py-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {isEdit
              ? 'Save changes'
              : `Create ${schema.singular.toLowerCase()}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
